import { useState, useRef, useEffect, memo } from 'react';
import { Trash2, MessageCircle, MessageCirclePlus, SquareArrowOutUpRight } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CellRenderer from './cells/CellRenderer';
import Tooltip from '../common/Tooltip';
import useBoardStore from '../../store/boardStore';
import useCardPanelStore from '../../store/cardPanelStore';

function CardRow({ card, columns, groupColor, selected, onToggleSelect, canCreate }) {
  const statusOptions = useBoardStore((s) => s.statusOptions);
  const users = useBoardStore((s) => s.users);
  const updateCard = useBoardStore((s) => s.updateCard);
  const archiveCard = useBoardStore((s) => s.archiveCard);
  const upsertCardValue = useBoardStore((s) => s.upsertCardValue);
  const isPendingMove = useBoardStore((s) => s.pendingCardMoves.has(card.id));
  const openCard = useCardPanelStore((s) => s.openCard);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `card:${card.id}`,
    data: { type: 'card', cardId: card.id, groupId: card.group_id },
    disabled: !canCreate,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : isPendingMove ? 0.5 : 1,
    pointerEvents: isPendingMove ? 'none' : undefined,
  };
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(card.title);
  const inputRef = useRef(null);

  useEffect(() => { setTitle(card.title); }, [card.title]);
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  const saveTitle = () => {
    setEditing(false);
    const trimmed = title.trim();
    if (trimmed && trimmed !== card.title) updateCard(card.id, { title: trimmed });
    else setTitle(card.title);
  };

  const myCardValues = useBoardStore((s) => s.cardValues);

  if (card.is_divider) {
    const dividerStyle = {
      backgroundColor: '#000',
      backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.7) 50%, transparent 50%)',
      backgroundSize: '10px 1px',
      backgroundRepeat: 'repeat-x',
      backgroundPosition: 'left center',
    };
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className="group/row border-b border-border/30"
      >
        <td className="relative sticky left-0 z-10" style={dividerStyle}>
          <div
            {...(canCreate ? attributes : {})}
            {...(canCreate ? listeners : {})}
            className={`flex items-center h-full min-h-[36px] ${canCreate ? 'cursor-grab active:cursor-grabbing' : ''}`}
          >
            <div className="w-1.5 self-stretch shrink-0" aria-hidden="true" />
            {canCreate && (
              <div
                className="flex items-center justify-center px-3 self-stretch shrink-0 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={(e) => { e.stopPropagation(); onToggleSelect?.(card.id); }}
              >
                <input
                  type="checkbox"
                  checked={!!selected}
                  onChange={() => onToggleSelect?.(card.id)}
                  className="w-[18px] h-[18px] rounded border-border bg-transparent accent-accent cursor-pointer pointer-events-none"
                />
              </div>
            )}
            <div className="flex-1 flex items-center gap-2 px-3 self-stretch">
              {editing ? (
                <input
                  ref={inputRef}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTitle();
                    if (e.key === 'Escape') { setTitle(card.title); setEditing(false); }
                  }}
                  placeholder="Nome da divisória..."
                  className="flex-1 bg-transparent border-none outline-none text-sm font-semibold uppercase tracking-wider text-white"
                />
              ) : (
                <span
                  onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                  className="flex-1 block min-h-[20px] text-sm font-semibold uppercase tracking-wider cursor-text truncate text-white"
                >
                  {card.title || '\u00A0'}
                </span>
              )}
              {canCreate && (
                <button
                  onClick={(e) => { e.stopPropagation(); archiveCard(card.id); }}
                  className="p-1 rounded opacity-0 group-hover/row:opacity-100 hover:bg-white/10 text-text-secondary hover:text-danger transition-all shrink-0"
                  title="Mover para lixeira"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </td>
        {columns.map((col) => (
          <td
            key={col.id}
            className="border-l border-border/20"
            style={{ ...dividerStyle, width: col.width || 150, minWidth: col.width || 150, maxWidth: col.width || 150 }}
          />
        ))}
        <td className="w-10" style={dividerStyle} />
      </tr>
    );
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group/row transition-all ${selected ? 'bg-accent/15 shadow-[inset_0_0_0_2px_rgba(87,155,252,0.55),inset_0_0_18px_rgba(87,155,252,0.25)]' : ''}`}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--group-hover)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = ''; }}
    >
      <td
        className="border-r border-b border-[color:var(--group-border)] relative cursor-pointer overflow-hidden sticky left-0 z-10"
        style={{ backgroundColor: 'var(--group-bg)' }}
        onClick={() => { if (!editing) openCard(card.id); }}
      >
        <div
          aria-hidden="true"
          className={`absolute inset-0 pointer-events-none transition-all ${
            selected
              ? 'bg-accent/15 shadow-[inset_0_0_0_2px_rgba(87,155,252,0.55),inset_0_0_18px_rgba(87,155,252,0.25)]'
              : 'group-hover/row:bg-[var(--group-hover)]'
          }`}
        />
        <div
          {...(canCreate ? attributes : {})}
          {...(canCreate ? listeners : {})}
          className={`relative flex items-center h-full ${canCreate ? 'cursor-grab active:cursor-grabbing' : ''}`}
        >
          <div className="w-1.5 self-stretch shrink-0" style={{ backgroundColor: groupColor }} />
          {canCreate && (
            <div
              className="flex items-center justify-center px-3 self-stretch shrink-0 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={(e) => { e.stopPropagation(); onToggleSelect?.(card.id); }}
            >
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect?.(card.id)}
                className="w-[18px] h-[18px] rounded border-border bg-transparent accent-accent cursor-pointer pointer-events-none"
              />
            </div>
          )}
          <div className="flex-1 flex items-center gap-2 px-3 min-h-[44px] min-w-0 border-l border-[color:var(--group-border)] self-stretch">
            {editing ? (
              <input
                ref={inputRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={saveTitle}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTitle();
                  if (e.key === 'Escape') { setTitle(card.title); setEditing(false); }
                }}
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-text-primary"
              />
            ) : (
              <Tooltip content={card.title} className="flex-1 min-w-0 block">
                <span
                  onClick={(e) => { if (!canCreate) return; e.stopPropagation(); setEditing(true); }}
                  className={`block text-sm text-text-primary transition-colors truncate ${canCreate ? 'cursor-text hover:text-accent' : ''}`}
                >
                  {card.title}
                </span>
              </Tooltip>
            )}

            <button
              onClick={() => openCard(card.id)}
              className="p-1 rounded opacity-0 group-hover/row:opacity-100 text-text-secondary/60 hover:text-accent hover:bg-white/5 transition-all shrink-0"
              title="Expandir card"
            >
              <SquareArrowOutUpRight className="w-3.5 h-3.5" />
            </button>
            {card.comment_count > 0 ? (
              <button
                onClick={() => openCard(card.id)}
                className="relative p-1 rounded text-accent hover:bg-accent/20 transition-all shrink-0"
                title={`${card.comment_count} ${card.comment_count === 1 ? 'atualização' : 'atualizações'}`}
              >
                <MessageCircle className="w-4 h-4 fill-accent/20" />
                <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[9px] leading-none min-w-[14px] h-[14px] rounded-full flex items-center justify-center px-0.5 font-semibold">
                  {card.comment_count > 99 ? '99+' : card.comment_count}
                </span>
              </button>
            ) : (
              <button
                onClick={() => openCard(card.id)}
                className="p-1 rounded text-text-secondary/60 hover:text-accent hover:bg-white/5 transition-all shrink-0"
                title="Adicionar atualização"
              >
                <MessageCirclePlus className="w-4 h-4" />
              </button>
            )}
            {canCreate && (
              <button
                onClick={(e) => { e.stopPropagation(); archiveCard(card.id); }}
                className="p-1 rounded opacity-0 group-hover/row:opacity-100 hover:bg-white/10 text-text-secondary hover:text-danger transition-all shrink-0"
                title="Mover para lixeira"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </td>
      {columns.map((col) => (
        <td
          key={col.id}
          className={`${col.type === 'status' || col.type === 'select' ? 'card-cell' : ''} border-r border-b border-[color:var(--group-border)] relative p-0`}
          style={{ width: col.width || 150, minWidth: col.width || 150, maxWidth: col.width || 150, height: '1px' }}
        >
          <CellRenderer
            column={col}
            card={card}
            value={myCardValues[`${card.id}:${col.id}`] ?? null}
            onChange={(val) => upsertCardValue(card.id, col.id, val)}
            statusOptions={statusOptions[col.id]}
            users={users}
            readOnly={!canCreate}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-0 group-hover/row:opacity-100 transition-opacity"
            style={{ backgroundColor: 'rgba(0,0,0,0.15)', zIndex: 1 }}
          />
        </td>
      ))}
      <td className="w-10 border-b border-[color:var(--group-border)]" />
    </tr>
  );
}

export default memo(CardRow, (prev, next) =>
  prev.card === next.card &&
  prev.columns === next.columns &&
  prev.groupColor === next.groupColor &&
  prev.selected === next.selected &&
  prev.canCreate === next.canCreate &&
  prev.onToggleSelect === next.onToggleSelect
);
